import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Axios instance configured with the API base URL and auth interceptors.
 * Used for all standard REST API calls through API Gateway.
 */
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach JWT access token to every request
api.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // If no session, proceed without token (will get 401)
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle common error patterns
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status } = error.response;

      // 401: redirect to login
      if (status === 401) {
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Extract API error body if available
      const apiError = error.response.data?.error;
      if (apiError) {
        const enrichedError = new Error(apiError.message || 'An error occurred');
        (enrichedError as unknown  as Record<string, unknown>).code = apiError.code;
        (enrichedError as unknown as Record<string, unknown>).details = apiError.details;
        (enrichedError as unknown as Record<string, unknown>).status = status;
        return Promise.reject(enrichedError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
